import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Save, CreditCard, Mail, CheckCircle2, AlertCircle, Send, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = {
  stripe_api_key: "",
  smtp_host: "",
  smtp_port: "587",
  smtp_encryption: "starttls",
  smtp_user: "",
  smtp_password: "",
  smtp_from: "info@netkom.fr",
  smtp_from_name: "WiLink",
  contact_email: "info@netkom.fr",
};

export default function IntegrationsManager() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showStripe, setShowStripe] = useState(false);
  const [showSmtp, setShowSmtp] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/integrations");
      setStatus(data);
      setForm((f) => ({
        ...f,
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || "587",
        smtp_encryption: data.smtp_encryption || "starttls",
        smtp_user: data.smtp_user || "",
        smtp_from: data.smtp_from || "info@netkom.fr",
        smtp_from_name: data.smtp_from_name || "WiLink",
        contact_email: data.contact_email || "info@netkom.fr",
        // secrets jamais pré-remplis
        stripe_api_key: "",
        smtp_password: "",
      }));
      setTestTo((t) => t || data.contact_email || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const saveStripe = async (e) => {
    e.preventDefault();
    if (!form.stripe_api_key.trim()) {
      return toast.error("Saisissez une clé Stripe (sk_test_... ou sk_live_...)");
    }
    setSavingStripe(true);
    try {
      const { data } = await api.patch("/admin/integrations", { stripe_api_key: form.stripe_api_key.trim() });
      setStatus(data);
      setForm((f) => ({ ...f, stripe_api_key: "" }));
      toast.success("Clé Stripe enregistrée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingStripe(false);
    }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    if (!form.smtp_host.trim() || !form.smtp_user.trim()) {
      return toast.error("Host et User sont requis.");
    }
    const payload = {
      smtp_host: form.smtp_host.trim(),
      smtp_port: form.smtp_port.trim() || "587",
      smtp_encryption: form.smtp_encryption || "starttls",
      smtp_user: form.smtp_user.trim(),
      smtp_from: form.smtp_from.trim(),
      smtp_from_name: form.smtp_from_name.trim(),
      contact_email: form.contact_email.trim(),
    };
    if (form.smtp_password.trim()) {
      payload.smtp_password = form.smtp_password.trim();
    }
    setSavingSmtp(true);
    try {
      const { data } = await api.patch("/admin/integrations", payload);
      setStatus(data);
      setForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Réglages SMTP enregistrés");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingSmtp(false);
    }
  };

  const testSmtp = async () => {
    if (!testTo.includes("@")) return toast.error("Email destinataire invalide");
    setTesting(true);
    try {
      const { data } = await api.post("/admin/integrations/test-smtp", { to: testTo.trim() });
      if (data?.sent) toast.success(`Email de test envoyé à ${data.to} ✓`);
      else toast.warning(`Échec : ${data?.reason || "raison inconnue"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="integrations-manager">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// WiLink commercial</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Intégrations</h1>
        <p className="text-sm text-muted-foreground mt-1">Clés Stripe et configuration SMTP. Valeurs stockées en base et chargées en environnement à chaque démarrage.</p>
      </div>

      {/* STRIPE */}
      <section className="border border-border bg-card rounded-md p-6" data-testid="section-stripe">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Stripe</h2>
              <p className="text-sm text-muted-foreground">Paiement unique 69€ via Stripe Checkout. Clé API secrète (test ou live).</p>
            </div>
          </div>
          {status && (
            status.stripe_configured ? (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Configuré
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 inline-flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Non configuré
              </span>
            )
          )}
        </div>

        {status?.stripe_configured && (
          <div className="bg-muted/40 rounded-md p-3 mb-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Clé actuelle</p>
            <code className="text-sm font-mono">{status.stripe_api_key_masked}</code>
          </div>
        )}

        <form onSubmit={saveStripe} className="space-y-3">
          <div>
            <Label htmlFor="stripe_api_key">Nouvelle clé Stripe (sk_test_... ou sk_live_...)</Label>
            <div className="relative">
              <Input
                id="stripe_api_key"
                type={showStripe ? "text" : "password"}
                placeholder="sk_live_..."
                value={form.stripe_api_key}
                onChange={(e) => setForm({ ...form, stripe_api_key: e.target.value })}
                className="font-mono pr-10"
                data-testid="stripe-key-input"
              />
              <button type="button" onClick={() => setShowStripe((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
                {showStripe ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Récupérable dans Stripe Dashboard → Développeurs → Clés API → <b>Clé secrète</b>. Pour les ventes réelles, utilisez la clé <code className="text-xs bg-muted px-1 rounded">sk_live_...</code>.
            </p>
          </div>
          <Button type="submit" disabled={savingStripe || !form.stripe_api_key.trim()} data-testid="stripe-save">
            <Save className="h-4 w-4 mr-2" /> {savingStripe ? "Enregistrement…" : "Enregistrer la clé Stripe"}
          </Button>
        </form>

        <p className="text-xs text-muted-foreground mt-4 border-t border-border pt-3">
          ℹ️ N&apos;oubliez pas de configurer le webhook Stripe sur votre URL : <code className="text-xs bg-muted px-1 rounded">https://votre-domaine.com/api/webhook/stripe</code> (événements : <code className="text-xs bg-muted px-1 rounded">checkout.session.completed</code>).
        </p>
      </section>

      {/* SMTP */}
      <section className="border border-border bg-card rounded-md p-6" data-testid="section-smtp">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">SMTP — Envoi d&apos;emails</h2>
              <p className="text-sm text-muted-foreground">Envoi des clés de licence aux acheteurs + notifications du formulaire contact. Compatible Gmail, OVH, etc.</p>
            </div>
          </div>
          {status && (
            status.smtp_configured ? (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Configuré
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 inline-flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Non configuré
              </span>
            )
          )}
        </div>

        <form onSubmit={saveSmtp} className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_100px_180px] gap-3">
            <div>
              <Label htmlFor="smtp_host">Hôte SMTP *</Label>
              <Input id="smtp_host" placeholder="ssl0.ovh.net  ou  smtp.gmail.com" value={form.smtp_host}
                onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} data-testid="smtp-host" />
            </div>
            <div>
              <Label htmlFor="smtp_port">Port *</Label>
              <Input id="smtp_port" placeholder="587" value={form.smtp_port}
                onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} data-testid="smtp-port" />
            </div>
            <div>
              <Label htmlFor="smtp_encryption">Chiffrement *</Label>
              <select
                id="smtp_encryption"
                value={form.smtp_encryption}
                onChange={(e) => setForm({ ...form, smtp_encryption: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="smtp-encryption"
              >
                <option value="starttls">STARTTLS (587)</option>
                <option value="ssl">SSL/TLS (465)</option>
                <option value="none">Aucun (déconseillé)</option>
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="smtp_user">Identifiant SMTP *</Label>
              <Input id="smtp_user" placeholder="info@votredomaine.com" value={form.smtp_user}
                onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} data-testid="smtp-user" />
            </div>
            <div>
              <Label htmlFor="smtp_password">
                Mot de passe SMTP {status?.smtp_password_set && <span className="text-emerald-600 text-xs">(déjà enregistré)</span>}
              </Label>
              <div className="relative">
                <Input
                  id="smtp_password"
                  type={showSmtp ? "text" : "password"}
                  placeholder={status?.smtp_password_set ? "Laisser vide pour conserver" : "Mot de passe ou App Password"}
                  value={form.smtp_password}
                  onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                  className="pr-10"
                  data-testid="smtp-password"
                />
                <button type="button" onClick={() => setShowSmtp((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
                  {showSmtp ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="smtp_from">Expéditeur (From) *</Label>
              <Input id="smtp_from" value={form.smtp_from}
                onChange={(e) => setForm({ ...form, smtp_from: e.target.value })} data-testid="smtp-from" />
            </div>
            <div>
              <Label htmlFor="smtp_from_name">Nom expéditeur</Label>
              <Input id="smtp_from_name" value={form.smtp_from_name}
                onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })} data-testid="smtp-from-name" />
            </div>
          </div>

          <div>
            <Label htmlFor="contact_email">Email de réception (formulaire contact)</Label>
            <Input id="contact_email" type="email" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="contact-email" />
            <p className="text-xs text-muted-foreground mt-1.5">Les messages du formulaire <code className="text-xs bg-muted px-1 rounded">/contact</code> sont envoyés à cette adresse.</p>
          </div>

          <Button type="submit" disabled={savingSmtp} data-testid="smtp-save">
            <Save className="h-4 w-4 mr-2" /> {savingSmtp ? "Enregistrement…" : "Enregistrer SMTP"}
          </Button>
        </form>

        {/* Test SMTP */}
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-sm font-bold mb-2">Test SMTP</h3>
          <p className="text-xs text-muted-foreground mb-3">Envoyez un email de test pour vérifier que la configuration fonctionne.</p>
          <div className="flex gap-2">
            <Input type="email" placeholder="vous@votre-email.com" value={testTo}
              onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to" className="max-w-xs" />
            <Button variant="outline" onClick={testSmtp} disabled={testing || !status?.smtp_configured} data-testid="smtp-test-btn">
              <Send className="h-4 w-4 mr-2" /> {testing ? "Envoi…" : "Envoyer un test"}
            </Button>
          </div>
          {!status?.smtp_configured && (
            <p className="text-xs text-muted-foreground mt-2">Le test sera disponible après avoir enregistré une configuration SMTP complète.</p>
          )}
        </div>

        <details className="mt-4 text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">💡 Aide configuration Gmail / OVH</summary>
          <div className="mt-3 space-y-3 text-xs text-muted-foreground bg-muted/30 rounded p-3 leading-relaxed">
            <div>
              <p className="font-bold text-foreground mb-1">Gmail</p>
              <p>Host: <code className="bg-muted px-1 rounded">smtp.gmail.com</code> · Port: <code className="bg-muted px-1 rounded">587</code></p>
              <p>Identifiant : votre adresse Gmail complète</p>
              <p>Mot de passe : <b>App Password</b> à générer dans Google Account → Sécurité → Validation en 2 étapes → Mots de passe d&apos;application</p>
            </div>
            <div>
              <p className="font-bold text-foreground mb-1">OVH</p>
              <p>Host: <code className="bg-muted px-1 rounded">ssl0.ovh.net</code> · Port: <code className="bg-muted px-1 rounded">587</code> (STARTTLS) ou <code className="bg-muted px-1 rounded">465</code> (SSL)</p>
              <p>Identifiant : votre adresse email OVH complète</p>
              <p>Mot de passe : celui de votre boîte email</p>
            </div>
          </div>
        </details>
      </section>
    </div>
  );
}
